import { useEffect, useMemo, useState } from 'react';
import { useApptActions } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { can, useAgenda, useCash, useClients, useDebts, useMe, type AgendaItem, type ClientRow } from '../lib/data.ts';
import { useWide } from '../app/desk.tsx';
import { addDays, agoLabel, dayLabel, dowIndex, dur, first, greeting, hhmm, isoLocal, longDate, nowLocal, toMin } from '../lib/format.ts';
import { useReminders, useWaitlist } from './Engage.tsx';
import { InstallCard } from '../app/pwa.tsx';
import { HeaderWordmark } from '../ui/Header.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Avatar } from '../ui/bits.tsx';
import { Mf, Mv, useHiddenMoney } from '../ui/money.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';

const DOW_KEY = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'] as const;

export function useClock(): { date: string; min: number } {
  const [n, setN] = useState(nowLocal());
  useEffect(() => { const t = window.setInterval(() => setN(nowLocal()), 30_000); return () => window.clearInterval(t); }, []);
  return n;
}

const startMin = (a: AgendaItem) => toMin(a.time);
// horário que passa da meia-noite termina "depois" do fim do dia, não às 01:00 de hoje
const endMin = (a: AgendaItem) => { const e = isoLocal(a.endsAt); return e.min + (e.date > a.date ? 24 * 60 : 0); };
const isLive = (a: AgendaItem) => a.status !== 'cancelled' && a.status !== 'no_show';

export function Hoje() {
  const nav = useNav();
  const act = useApptActions();
  const me = useMe().data!;
  const { date: today, min: now } = useClock();
  const agenda = useAgenda(today);
  const cash = useCash(can(me, 'valoresSalao'));
  const maintenance = useClients('', 'maintenance');
  const waitlist = useWaitlist(today).data ?? [];
  const [hid] = useHiddenMoney();
  const [showPast, setShowPast] = useState(false);
  const [showStale, setShowStale] = useState(false);
  const wide = useWide();
  const settings = me.salon.settings;
  const day = settings.hours[DOW_KEY[dowIndex(today)]!];
  // fim do expediente: o horário do salão, ou o fim do último atendimento, o que for mais tarde
  const lastEnd = (agenda.data ?? []).filter((a) => a.status !== 'cancelled').reduce((m, a) => Math.max(m, isoLocal(a.endsAt).min), 0);
  const close = Math.max(toMin(day.fim), lastEnd);
  const mine = (a: AgendaItem) => !me.professionalId || a.professionalId === me.professionalId;

  const list = useMemo(() => (agenda.data ?? []).filter(mine).sort((x, y) => x.time.localeCompare(y.time)), [agenda.data, me.professionalId]);
  const all = list.filter(isLive);
  const cur = all.find((a) => a.status === 'in_service') ?? all.find((a) => a.status === 'arrived') ?? null;
  const done = all.filter((a) => a.status === 'completed');
  const open = all.filter((a) => a.status !== 'completed');
  const waitList = open.filter((a) => a.status === 'pending' || a.status === 'confirmed');
  const nextUp = waitList.find((a) => now - startMin(a) <= 60) ?? waitList[0] ?? null;
  const dayOver = all.length > 0 && open.length === 0;
  const missed = list.filter((a) => a.status === 'no_show');

  // sem dados ainda
  // o Hoje abre no "agora": se quem está na cadeira (ou a próxima) ficou abaixo da dobra, rola até ela uma vez
  const focusId = cur?.id ?? nextUp?.id ?? null;
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (focused || !focusId || wide) return;
    const el = document.getElementById('ap-' + focusId);
    if (!el) return;
    setFocused(true);
    const r = el.getBoundingClientRect();
    if (r.top > window.innerHeight * 0.6) window.scrollTo({ top: window.scrollY + r.top - window.innerHeight * 0.3, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focusId, focused, wide, agenda.data]);

  // sem a agenda, o Hoje não diz "dia livre": diz que não carregou
  if (agenda.data === undefined) return <div className="hoje-enter"><HeaderWordmark withEye />{agenda.isError ? <Failed error={agenda.error} retry={() => agenda.refetch()} /> : <Skeleton kind="card" rows={4} />}</div>;

  // trechos livres do agora até o fim do expediente
  type Item = { type: 'appt'; a: AgendaItem; s: number } | { type: 'gap'; s: number; e: number } | { type: 'miss'; a: AgendaItem; s: number } | { type: 'now'; s: number } | { type: 'lunch'; s: number };
  const gaps: Item[] = [];
  if (day.on) {
    const busy = open.map((a) => [startMin(a), endMin(a)] as const).concat(settings.lunch.on ? [[toMin(settings.lunch.ini), toMin(settings.lunch.fim)] as const] : []).sort((x, y) => x[0] - y[0]);
    let t = Math.max(now, toMin(day.ini));
    for (const b of busy) {
      if (b[1] <= t) continue;
      if (b[0] - t >= 30) gaps.push({ type: 'gap', s: t, e: b[0] });
      t = Math.max(t, b[1]);
    }
    if (close - t >= 30) gaps.push({ type: 'gap', s: t, e: close });
  }
  const items: Item[] = [
    ...open.map((a) => ({ type: 'appt' as const, a, s: startMin(a) })),
    ...gaps,
    ...missed.map((a) => ({ type: 'miss' as const, a, s: startMin(a) })),
  ];
  if (!cur && !gaps.some((g) => g.s <= now + 1)) items.push({ type: 'now', s: now });
  if (settings.lunch.on && toMin(settings.lunch.ini) >= now) items.push({ type: 'lunch', s: toMin(settings.lunch.ini) });
  const order = { now: 0, appt: 1, miss: 2, gap: 3, lunch: 4 };
  items.sort((x, y) => x.s - y.s || order[x.type] - order[y.type]);

  const staleList = open.filter((a) => (a.status === 'pending' || a.status === 'confirmed') && now - startMin(a) > 60);
  const hideStale = staleList.length >= 2 && !showStale ? staleList.slice(0, -1) : [];
  let staleBand = false;
  let firstGap = true;

  const rows: React.ReactNode[] = [];
  items.forEach((it, i) => {
    const style = { '--i': i } as React.CSSProperties;
    if (it.type === 'now') {
      rows.push(
        <div key="now" className="r-nowline" style={style}><span className="r-time">{hhmm(now)}</span><span className="r-rail"><i /></span>
          <div className="r-nl"><b>Agora</b><span>{nextUp && startMin(nextUp) <= now ? `esperando ${first(nextUp.client)}` : `livre até ${nextUp ? nextUp.time : hhmm(close)}`}</span><i aria-hidden="true" /></div></div>,
      );
      return;
    }
    if (it.type === 'miss') {
      rows.push(
        <div key={'m' + it.a.id} className="r-item r-miss" style={style}><span className="r-time">{it.a.time}</span><span className="r-rail"><i className="miss" /></span>
          <div className="r-body"><div className="r-card"><div className="r-row1"><div className="r-main">
            <button className="r-name" onClick={() => nav.open({ kind: 'appt', id: it.a.id })} aria-label={`${it.a.client}, faltou. Abrir`}>
              <b>{it.a.client}</b><small>{it.a.service} · faltou{it.a.depositStatus === 'retained' ? ' · sinal retido' : it.a.depositStatus === 'credited' ? ' · sinal virou crédito' : ''}</small>
            </button></div></div></div></div></div>,
      );
      return;
    }
    if (it.type === 'lunch') {
      rows.push(<div key="lunch" className="r-lunch" style={style}><span className="r-time">{hhmm(it.s)}</span><span className="r-rail" /><span>Almoço</span></div>);
      return;
    }
    if (it.type === 'gap') {
      const fromNow = it.s <= now + 1;
      const showBtn = firstGap; firstGap = false;
      const encaixe = () => nav.open({ kind: 'novo', date: today, time: hhmm(Math.ceil(it.s / 15) * 15), professionalId: me.professionalId ?? undefined });
      rows.push(
        <div key={'g' + it.s} className="r-gap" style={style}><span className="r-time">{hhmm(it.s)}</span><span className="r-rail" />
          {showBtn
            ? <div className="r-gap-body"><span><b>{fromNow ? 'Livre agora' : 'Livre'}</b> até {hhmm(it.e)} · {dur(it.e - it.s)}</span><button onClick={encaixe}><Icon name="plus" />Encaixar</button></div>
            : <button className="r-gap-body quiet" onClick={encaixe} aria-label={`Encaixar entre ${hhmm(it.s)} e ${hhmm(it.e)}`}><span><b>Livre</b> até {hhmm(it.e)} · {dur(it.e - it.s)}</span></button>}
        </div>,
      );
      // quem está na lista de espera para hoje e cabe neste horário livre
      const fits = showBtn ? waitlist.filter((w) => w.durationMin <= it.e - it.s && (!w.professionalId || !me.professionalId || w.professionalId === me.professionalId)) : [];
      if (fits.length) rows.push(
        <div key={'w' + it.s} className="r-wait-row" style={style}><span className="r-time" /><span className="r-rail" />
          <button className="r-wait" onClick={() => nav.go('espera', { date: today, time: hhmm(Math.ceil(Math.max(it.s, now) / 15) * 15) })}>
            <span>{fits.length} {fits.length > 1 ? 'clientes na lista de espera' : 'cliente na lista de espera'}</span><b>Avisar</b></button></div>,
      );
      return;
    }
    const a = it.a;
    if (hideStale.includes(a)) {
      if (!staleBand) {
        staleBand = true;
        const ts = hideStale.map((x) => x.time);
        rows.push(
          <div key="stale" className="r-stale" style={style}><span className="r-time">{ts[0]}</span><span className="r-rail"><i /></span>
            <button className="r-stale-b" onClick={() => setShowStale(true)}><span><b>{ts.length} sem desfecho</b> · {ts.length > 1 ? `${ts.slice(0, -1).join(', ')} e ${ts.at(-1)}` : ts[0]}</span><em>Resolver</em></button></div>,
        );
      }
      return;
    }
    if (a === cur) { rows.push(<NowBlock key={a.id} a={a} now={now} all={all} style={style} />); return; }
    rows.push(<ApptRow key={a.id} a={a} now={now} isNext={a === nextUp} all={all} tolerance={settings.toleranceMin} style={style} act={act} />);
  });
  rows.push(<div key="end" className="r-end" style={{ '--i': items.length } as React.CSSProperties}><span className="r-time">{hhmm(close)}</span><span className="r-rail"><i /></span><span>Fim do expediente</span></div>);

  // resumo do dia: concluído · atendendo · a seguir
  const value = (a: AgendaItem) => a.priceCents;
  const rec = done.reduce((s, a) => s + value(a), 0);
  const nowList = all.filter((a) => a.status === 'in_service');
  const futList = all.filter((a) => a.status === 'pending' || a.status === 'confirmed' || a.status === 'arrived');
  const waiting = all.filter((a) => a.status === 'arrived').length;
  const nowDue = nowList.reduce((s, a) => s + value(a), 0);
  const futDue = futList.reduce((s, a) => s + value(a), 0);
  const morning = !done.length && !cur && nextUp && startMin(nextUp) > now;

  // o resumo do dia é uma linha: quanto já entrou, quanto o dia promete e quem está esperando
  const who = waiting ? `${waiting} na recepção` : nowList.length ? `${nowList.length > 1 ? `${nowList.length} atendimentos` : 'atendendo'} agora` : 'ninguém esperando';
  let tri: React.ReactNode = (
    <button className="rs rs-one" onClick={() => (can(me, 'valoresSalao') ? nav.go('caixa') : nav.go('agenda'))}>
      <span><b><Mv c={rec} /></b> de <Mv c={rec + nowDue + futDue} /></span>
      <span className="rs-dot" aria-hidden="true">·</span>
      <span>{done.length} de {all.length} {all.length === 1 ? 'atendimento' : 'atendimentos'}</span>
      <span className="rs-dot" aria-hidden="true">·</span>
      <span className={waiting ? 'rs-wait' : ''}>{who}</span>
      {morning && <small className="rs-hint">previsto para hoje</small>}
    </button>
  );
  if (!all.length) tri = <EmptyDay today={today} mine={mine} />;
  if (dayOver) {
    const byMethod = cash.data?.byMethod ?? [];
    const label: Record<string, string> = { pix: 'Pix', credit: 'Cartão', debit: 'Cartão', cash: 'Dinheiro', client_credit: 'Crédito da cliente', later: 'Pagar depois' };
    const by = new Map<string, number>();
    byMethod.forEach((m) => by.set(label[m.method] ?? m.method, (by.get(label[m.method] ?? m.method) ?? 0) + m.amount));
    tri = (
      <div className="rs rs-fim">
        <small>Dia encerrado · {done.length} {done.length > 1 ? 'atendimentos' : 'atendimento'}</small><b><Mv c={rec} /></b>
        {by.size > 0 && <div className="fim-rows">{[...by].map(([k, v]) => <div key={k}><span>{k}</span><b><Mf c={v} /></b></div>)}</div>}
        {can(me, 'valoresSalao') && (cash.data?.session ? <button className="fim-cta" onClick={() => { nav.go('caixa'); nav.open({ kind: 'fechar' }); }}>Fechar o caixa</button> : <p className="fim-closed">Caixa fechado</p>)}
        <Tomorrow today={today} mine={mine} />
      </div>
    );
  }

  const attn = [
    ...open.filter((a) => (a.status === 'pending' || a.status === 'confirmed') && now - startMin(a) >= 5 && now - startMin(a) <= 60)
      .map((a) => ({ id: a.id, txt: <><b>{first(a.client)}</b> está {now - startMin(a)} min atrasada</> })),
    ...open.filter((a) => a.status === 'pending' && startMin(a) - now > -10 && startMin(a) - now <= 120)
      .map((a) => ({ id: a.id, txt: <><b>{first(a.client)}</b> ainda não confirmou o horário das {a.time}</> })),
    ...open.filter((a) => a.depositStatus === 'awaiting')
      .map((a) => ({ id: a.id, txt: <><b>{first(a.client)}</b> {a.depositDueAt && new Date(a.depositDueAt) < new Date() ? 'passou do prazo do sinal' : 'está aguardando o sinal'}</> })),
  ];
  const ret = maintenance.data ?? [];

  const attnEl = attn.length > 0 && (
    <div className="attn-list" role="status">
      {attn.map((x, i) => (
        <button key={x.id + i} className="attn" onClick={() => document.getElementById('ap-' + x.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}><span>{x.txt}</span><em>Ver</em></button>
      ))}
    </div>
  );
  const pastEl = done.length > 0 && <>
    <button className="r-past" aria-expanded={showPast} onClick={() => setShowPast(!showPast)}>
      <span className="r-past-seals" aria-hidden="true">{done.map((a) => <i key={a.id} />)}</span>
      <span>{dayOver ? `Os ${done.length} atendimentos de hoje` : `${done.length} ${done.length > 1 ? 'concluídos' : 'concluído'} mais cedo`}</span>
      <span className="r-past-chev"><Icon name="chev" /></span>
    </button>
    {showPast && (
      <div className="r-past-list">
        {done.map((a) => (
          <button key={a.id} className="r-past-row" onClick={() => nav.open({ kind: 'appt', id: a.id })}><span>{a.time}</span><b>{a.client}<small>{a.service}</small></b><em><Mf c={a.priceCents} /></em></button>
        ))}
      </div>
    )}
  </>;
  // horários que já passaram há mais de uma hora e seguem abertos: cada cartão tem Receber e Não veio
  const stuck = waitList.filter((x) => now - startMin(x) > 60).length;
  const riverEl = !dayOver && all.length > 0 && <>{stuck > 0 && <div className="note warn"><Icon name="info" /><span>{stuck === 1 ? '1 horário de hoje ficou sem fechar' : `${stuck} horários de hoje ficaram sem fechar`}. Em cada um, toque em Receber ou em Não veio.</span></div>}<div className="river">{rows}</div></>;
  const retEl = ret.length > 0 && (
    <button className="ret-card" onClick={() => nav.open({ kind: 'retorno' })}>
      <span><small>Manutenção</small><b>{ret.length} {ret.length > 1 ? 'clientes para chamar de volta' : 'cliente para chamar de volta'}</b>
        <em>{ret.slice(0, 3).map((c) => first(c.name)).join(', ')}{ret.length > 3 ? ` e mais ${ret.length - 3}` : ''}</em></span>
      <Icon name="chevR" />
    </button>
  );

  return (
    <div className={'hoje-enter' + (hid ? ' money-hidden' : '')}>
      <HeaderWordmark withEye />
      <InstallCard />
      <div className="today-head"><span className="eyebrow">{longDate(today)}</span><h1>{greeting(now)}, <b>{first(me.user.name)}</b></h1>
        <button className="th-entry" onClick={() => nav.open({ kind: 'temHorario' })}><Icon name="cal" />Tem horário?</button></div>
      {wide ? (
        // computador: o dia à esquerda (com os avisos em cima); resumo e o que vem depois à direita
        <div className="hj-cols">
          <div className="hj-main">{attnEl}{pastEl}{riverEl}{(dayOver || !all.length) && tri}</div>
          <DeskAside today={today} mine={mine} ret={ret} day={{ done: done.length, total: all.length, rec, due: nowDue + futDue, waiting }} />
        </div>
      ) : <>{now >= 16 * 60 && <LembretesCard today={today} />}{attnEl}{tri}{pastEl}{riverEl}{now < 16 * 60 && <LembretesCard today={today} />}{retEl}</>}
    </div>
  );
}

// celular: atalho para avisar as clientes de amanhã (no computador, o cartão Amanhã já leva para a mesma tela)
function LembretesCard({ today }: { today: string }) {
  const nav = useNav();
  const tomorrow = addDays(today, 1);
  const rows = useReminders(tomorrow).data ?? [];
  if (!rows.length) return null;
  const pend = rows.filter((r) => !r.sentAt && r.phone && r.whatsapp).length;
  return (
    <button className="ret-card" onClick={() => nav.go('lembretes', { date: tomorrow })}>
      <span><small>Lembretes</small><b>{pend ? `${pend} ${pend > 1 ? 'clientes sem aviso' : 'cliente sem aviso'} para amanhã` : 'Todas avisadas para amanhã'}</b>
        <em>{rows.length} {rows.length > 1 ? 'horários' : 'horário'} amanhã</em></span>
      <Icon name="chevR" />
    </button>
  );
}

function NowBlock({ a, now, all, style }: { a: AgendaItem; now: number; all: AgendaItem[]; style: React.CSSProperties }) {
  const nav = useNav();
  const act = useApptActions();
  const inService = a.status === 'in_service';
  const start = startMin(a), end = endMin(a);
  const pct = Math.max(0, Math.min(1, (now - start) / Math.max(1, end - start)));
  const left = Math.max(0, end - now);
  const deposit = a.depositStatus === 'received' ? a.depositCents : 0;
  const nx = all.filter((x) => ['confirmed', 'pending', 'arrived'].includes(x.status) && startMin(x) >= start && x.id !== a.id).sort((x, y) => startMin(x) - startMin(y))[0];
  const arrivedAt = a.arrivedAt ? isoLocal(a.arrivedAt).time : a.time;
  const live = inService
    ? now > end ? `Passou ${now - end} min do previsto` : left <= 5 ? `Termina em ${left} min${nx ? ` · ${first(nx.client)} às ${nx.time}` : ''}` : `Em atendimento · faltam ${left} min`
    : `Na recepção desde ${arrivedAt}`;
  return (
    <div className="r-now-wrap" style={style} id={'ap-' + a.id}>
      <span className="r-time">{a.time}</span><span className="r-rail"><i className="now" /></span>
      <div className="r-now nw3 nw4">
        <button className="nw3-main" onClick={() => nav.open({ kind: 'appt', id: a.id })} aria-label={`Abrir o atendimento de ${a.client}`}>
          <span className="nw3-live"><span className="dot-live" />{live}</span>
          <b>{a.client}</b><small>{a.service} · {inService ? `até ${hhmm(end)}` : 'pode começar agora'}</small>
        </button>
        {inService && <div className="nw3-bar" aria-hidden="true"><i style={{ width: `${(pct * 100).toFixed(1)}%` }} /></div>}
        <div className="nw4-foot">
          <span className="nw4-info">{inService ? <button className="chair-entry" onClick={() => nav.open({ kind: 'cadeira', id: a.id })}>Modo cadeira</button> : 'Esperando você'}</span>
          {inService
            ? <button className="nw4-pay" onClick={() => nav.open({ kind: 'comanda', id: a.id })}>Receber <b><Mf c={Math.max(0, a.priceCents - deposit)} /></b></button>
            : <button className="nw4-pay" onClick={() => act.start(a)}>Iniciar</button>}
        </div>
      </div>
    </div>
  );
}

function ApptRow({ a, now, isNext, all, tolerance, style, act }: { a: AgendaItem; now: number; isNext: boolean; all: AgendaItem[]; tolerance: number; style: React.CSSProperties; act: ReturnType<typeof useApptActions> }) {
  const nav = useNav();
  const reception = !!useMe().data?.salon.settings.hasReception;
  const live = a.status === 'in_service', here = a.status === 'arrived';
  const lateMin = a.status === 'pending' || a.status === 'confirmed' ? now - startMin(a) : 0;
  const late = lateMin >= 5, stale = lateMin > 60, overTol = lateMin >= tolerance;
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
  let tag: React.ReactNode, acts: React.ReactNode = null, dot = '';
  if (live) {
    tag = <em className="r-tag live">Em atendimento · {Math.max(0, endMin(a) - now)} min restantes</em>;
    acts = <div className="r-acts"><button className="solid" onClick={stop(() => nav.open({ kind: 'comanda', id: a.id }))}>Receber</button></div>;
    dot = 'now';
  } else if (here) {
    // só a cadeira da mesma profissional segura a cliente; hora que já passou vira "está terminando"
    const inChair = all.find((x) => x.status === 'in_service' && x.professionalId === a.professionalId);
    const freeAt = inChair ? endMin(inChair) : 0;
    tag = <em className="r-tag here">Na recepção desde {a.arrivedAt ? isoLocal(a.arrivedAt).time : a.time}{inChair ? (freeAt > now ? ` · pode começar às ${hhmm(freeAt)}, quando a ${first(inChair.client)} terminar` : ` · a ${first(inChair.client)} está terminando`) : ''}</em>;
    acts = inChair ? null : <div className="r-acts"><button className="solid" onClick={stop(() => act.start(a))}>Iniciar</button></div>;
    dot = 'here';
  } else {
    const soon = startMin(a) - now <= 30;
    const awaiting = a.depositStatus === 'awaiting';
    const overdue = awaiting && a.depositDueAt && new Date(a.depositDueAt) < new Date();
    const meta = [awaiting ? (overdue ? 'Prazo do sinal venceu' : 'Aguardando o sinal') : a.status === 'pending' ? 'A confirmar' : 'Confirmada'];
    if (a.source === 'public') meta.push('agendou online');
    if (a.depositStatus === 'received') meta.push(`sinal pago`);
    // ela avisou pelo link que vai atrasar: o cartão diz a hora prevista, sem mudar nada na agenda
    const eta = a.lateNoticeMin ? startMin(a) + a.lateNoticeMin : 0;
    tag = eta && now < eta + 10
      ? <em className="r-tag here"><b>Avisou que atrasa</b> · chega por volta das {hhmm(eta)}</em>
      : late
      ? stale ? <em className="r-tag late"><b>Não veio?</b> Era às {a.time} · há {dur(lateMin)}</em>
        : <em className="r-tag late">{lateMin} min atrasada{overTol ? ' · passou da tolerância' : ` · tolerância até ${hhmm(startMin(a) + tolerance)}`}</em>
      : <em className={'r-tag' + (a.status === 'pending' ? ' pend' : '')}><b>{meta[0]}</b>{meta.length > 1 ? ` · ${meta.slice(1).join(' · ')}` : ''}</em>;
    // o botão começa pelo nome: com duas na recepção, o toque não cai na cliente errada
    // sem recepção, o toque vai direto para o pagamento: não há Chegou nem Começar no meio
    const receive = () => nav.open({ kind: 'comanda', id: a.id });
    const arrived = reception
      ? <button className="solid" onClick={stop(() => act.arrived(a))}><Icon name="check" />{first(a.client)} chegou</button>
      : <button className="solid" onClick={stop(receive)}><Icon name="check" />Receber {first(a.client)}</button>;
    if (stale) acts = <div className="r-acts"><button onClick={stop(reception ? () => act.arrived(a) : receive)}>{reception ? 'Chegou' : 'Receber'}</button><button className="solid" onClick={stop(() => act.noShow(a))}>Não veio</button></div>;
    else if (late) acts = <div className="r-acts three"><button onClick={stop(() => act.remind(a, `Oi, ${first(a.client)}! Estamos te esperando para o horário das ${a.time}. Está a caminho?`))}>Mensagem</button><button onClick={stop(() => act.noShow(a))}>Não veio</button>{arrived}</div>;
    else if (isNext && (a.status === 'pending' || soon)) acts = (
      <div className="r-acts">
        {a.status === 'pending' && <button onClick={stop(() => act.askConfirmation(a))}><Icon name="message" />Pedir confirmação</button>}
        {soon && !reception && <button onClick={stop(() => act.noShow(a))}>Não veio</button>}
        {soon && arrived}
      </div>
    );
    dot = a.status === 'pending' ? 'pend' : '';
    if (isNext && !soon && !late && startMin(a) - now <= 60) acts = <>{acts}<div className="r-early"><span>Chegou antes?</span><button onClick={stop(reception ? () => act.arrived(a) : receive)}>{reception ? 'Chegou' : 'Receber'}</button></div></>;
  }
  return (
    <div id={'ap-' + a.id} className={'r-item' + (isNext ? ' next' : '') + (late ? ' late' : '') + (live ? ' inservice' : '') + (here ? ' here' : '')} style={style}>
      <span className="r-time">{a.time}</span><span className="r-rail"><i className={dot} /></span>
      <div className="r-body"><div className="r-swipe"><div className="r-card">
        <div className="r-row1"><div className="r-main">
          <button className="r-name" onClick={() => nav.open({ kind: 'appt', id: a.id })} aria-label={`${a.client}, ${a.time}. Abrir`}>
            <b>{a.client}</b><small>{a.service} · até {hhmm(endMin(a))}</small>
          </button>{tag}
        </div></div>
        {acts}
      </div></div></div>
    </div>
  );
}

function EmptyDay({ today, mine }: { today: string; mine: (a: AgendaItem) => boolean }) {
  const nav = useNav();
  const next = useAgenda(addDays(today, 1), addDays(today, 14));
  const nd = (next.data ?? []).filter(mine).filter(isLive)[0];
  return (
    <div className="rs rs-folga"><small>Hoje sem horários</small>
      {nd ? <><b>Próximo: {dayLabel(nd.date, today)} às {nd.time}</b><span>{nd.client} · {nd.service}</span><button onClick={() => nav.go('agenda', { date: nd.date })}>Abrir a agenda</button></>
        : <><b>Nada marcado nas próximas duas semanas</b><span>Agende alguém ou mande o link para as clientes marcarem sozinhas.</span>
          <div className="empty-acts"><button className="cta sm" onClick={() => nav.open({ kind: 'novo' })}>Agendar</button></div></>}
    </div>
  );
}

function Tomorrow({ today, mine }: { today: string; mine: (a: AgendaItem) => boolean }) {
  const t = useAgenda(addDays(today, 1));
  const l = (t.data ?? []).filter(mine).filter(isLive);
  return <div className="fim-tm"><small>Amanhã</small>{l.length ? l.slice(0, 3).map((x) => <span key={x.id}>{x.time} · {x.client} · {x.service}</span>) : <span>Agenda livre</span>}</div>;
}

// computador: coluna da direita. Todos os cartões com o mesmo desenho: título, ação à direita, linhas iguais.
function DeskAside({ today, mine, ret, day }: {
  today: string; mine: (a: AgendaItem) => boolean; ret: ClientRow[];
  day: { done: number; total: number; rec: number; due: number; waiting: number };
}) {
  const nav = useNav();
  const me = useMe().data!;
  const [hid, toggleHid] = useHiddenMoney();
  const tomorrow = addDays(today, 1);
  const t = useAgenda(tomorrow);
  const debts = useDebts();
  const wait = useWaitlist().data ?? [];
  const l = (t.data ?? []).filter(mine).filter(isLive).sort((x, y) => x.time.localeCompare(y.time));
  const unconfirmed = l.filter((a) => a.status === 'pending').length;
  const waiting = wait.filter((w) => w.status === 'waiting').length;
  const owed = (debts.data ?? []).reduce((s, d) => s + d.debt, 0);
  const money = can(me, 'valoresSalao');
  const pct = day.total ? Math.round((day.done / day.total) * 100) : 0;
  return (
    <aside className="hj-side" aria-label="Resumo e próximos dias">
      {day.total > 0 && (
        <section className="hj-box">
          <header><b>Resumo do dia</b>
            <button className="hj-eye" onClick={toggleHid} aria-pressed={hid} aria-label={hid ? 'Mostrar valores' : 'Esconder valores'} title={hid ? 'Mostrar valores' : 'Esconder valores'}><Icon name={hid ? 'eyeOff' : 'eye'} /></button>
          </header>
          <div className="hj-stats">
            <div><span>Atendimentos</span><strong>{day.done} <small>de {day.total}</small></strong><i className="hj-prog" aria-hidden="true"><i style={{ width: `${pct}%` }} /></i></div>
            <div><span>Concluído</span><strong><Mv c={day.rec} /></strong></div>
            <div><span>A seguir</span><strong><Mv c={day.due} /></strong></div>
          </div>
          {day.waiting > 0 && <p className="hj-foot"><i className="dot-live" aria-hidden="true" />{day.waiting} {day.waiting > 1 ? 'clientes na recepção' : 'cliente na recepção'}</p>}
        </section>
      )}

      <section className="hj-box">
        <header><b>Amanhã</b><span className="hj-acts"><button className="hj-act" onClick={() => nav.go('lembretes', { date: tomorrow })}>Lembretes</button><button className="hj-act" onClick={() => nav.go('agenda', { date: tomorrow })}>Ver agenda</button></span></header>
        {!l.length ? <p className="hj-empty">Nada marcado ainda.</p> : <>
          <p className="hj-sub">{l.length} {l.length > 1 ? 'horários' : 'horário'} · {unconfirmed ? <em>{unconfirmed} sem confirmar</em> : 'todos confirmados'}</p>
          <div className="hj-list">{l.slice(0, 5).map((a) => (
            <button key={a.id} className="hj-row" onClick={() => nav.open({ kind: 'appt', id: a.id })}>
              <span className="hj-t">{a.time}</span><span className="hj-n"><b>{a.client}</b><small>{a.service}</small></span>
              {a.status === 'pending' ? <em className="hj-tag">a confirmar</em> : <span className="hj-ok" aria-label="confirmado"><Icon name="check" /></span>}
            </button>
          ))}</div>
          {l.length > 5 && <button className="hj-more" onClick={() => nav.go('agenda', { date: tomorrow })}>Mais {l.length - 5} na agenda</button>}
        </>}
      </section>

      {ret.length > 0 && (
        <section className="hj-box">
          <header><b>Chamar de volta</b><button className="hj-act" onClick={() => nav.open({ kind: 'retorno' })}>Ver {ret.length > 3 ? `as ${ret.length}` : 'todas'}</button></header>
          <p className="hj-sub">Manutenção vencendo, sem horário marcado</p>
          <div className="hj-list">{ret.slice(0, 3).map((c) => (
            <button key={c.id} className="hj-row" onClick={() => nav.go('cliente', { id: c.id })}>
              <Avatar id={c.id} name={c.name} /><span className="hj-n"><b>{c.name}</b><small>{c.last_visit ? `última vez ${agoLabel(c.days_away ?? 0)}` : 'ainda sem visita'}</small></span>
            </button>
          ))}</div>
        </section>
      )}

      {(waiting > 0 || (money && owed > 0)) && (
        <section className="hj-box">
          <header><b>Pendências</b></header>
          <div className="hj-list">
            {waiting > 0 && <button className="hj-row" onClick={() => nav.go('espera')}><span className="hj-ic"><Icon name="clip" /></span><span className="hj-n"><b>Lista de espera</b><small>avise quando abrir um horário</small></span><em className="hj-num">{waiting}</em></button>}
            {money && owed > 0 && <button className="hj-row" onClick={() => nav.go('pendencias')}><span className="hj-ic"><Icon name="card" /></span><span className="hj-n"><b>Pagar depois</b><small>{(debts.data ?? []).length} {(debts.data ?? []).length > 1 ? 'clientes devendo' : 'cliente devendo'}</small></span><em className="hj-num"><Mv c={owed} /></em></button>}
          </div>
        </section>
      )}
    </aside>
  );
}
